import { useEffect, useRef } from 'react';

// URL exacta que exige Cloudflare. Su documentación avisa de que servir api.js
// desde otro sitio (proxy o copia en /assets) rompe el widget en cuanto lo
// actualizan, así que no se empaqueta con Vite. `render=explicit` porque el
// widget se monta y desmonta con el componente, no al cargar la página.
export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-api';

// Una sola promesa de carga para todos los widgets. Sin ella, cada montaje
// (StrictMode monta dos veces; el reintento tras un error remonta) colgaba sus
// propios listeners del mismo <script>, y se quedaban ahí para siempre.
let promesaCarga = null;

function cargarTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (promesaCarga) return promesaCarga;
  promesaCarga = new Promise((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(window.turnstile), { once: true });
    script.addEventListener('error', (e) => {
      // Se olvida la promesa y el <script> fallido para que el siguiente montaje
      // pueda reintentar: un <script> que ya dio error no vuelve a disparar nada.
      promesaCarga = null;
      script.remove();
      reject(e);
    }, { once: true });
  });
  return promesaCarga;
}

/**
 * Widget Cloudflare Turnstile. Entrega el token por `onToken` y lo vacía cuando
 * caduca (5 min) o falla. Para pedir un token nuevo se remonta cambiando su
 * `key`: un token solo se puede verificar una vez.
 */
export default function TurnstileWidget({ siteKey, onToken }) {
  const contenedor = useRef(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let widgetId;
    let desmontado = false;
    cargarTurnstile()
      .then((turnstile) => {
        if (desmontado || !turnstile || !contenedor.current) return;
        widgetId = turnstile.render(contenedor.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
        });
      })
      // Si el script no carga (bloqueador, red, CSP) el formulario sigue
      // funcionando: es el backend, según CAPTCHA_ENFORCEMENT, quien decide si
      // un alta sin token pasa.
      .catch(() => {});
    return () => {
      desmontado = true;
      if (widgetId !== undefined) window.turnstile?.remove(widgetId);
    };
  }, [siteKey]);

  return <div ref={contenedor} data-testid="turnstile-widget" style={{ display: 'flex', justifyContent: 'center' }} />;
}
